import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Hook to preserve query parameters across navigation
 * @param paramsToPreserve - Array of query param names to preserve (default: all params)
 */
export function usePreserveQueryParams(paramsToPreserve?: string[]) {
  const location = useLocation()

  const currentParams = useMemo(() => {
    const params = new URLSearchParams(location.search)

    // If specific params specified, filter to just those
    if (paramsToPreserve) {
      const filtered = new URLSearchParams()
      paramsToPreserve.forEach(key => {
        const value = params.get(key)
        if (value !== null) {
          filtered.set(key, value)
        }
      })
      return filtered
    }

    return params
  }, [location.search, paramsToPreserve])

  /**
   * Adds preserved query params to a path
   * @param path - The destination path
   * @param additionalParams - Optional additional params to add
   */
  const withParams = (path: string, additionalParams?: Record<string, string>) => {
    const params = new URLSearchParams(currentParams)

    // Add any additional params
    if (additionalParams) {
      Object.entries(additionalParams).forEach(([key, value]) => {
        params.set(key, value)
      })
    }

    const queryString = params.toString()
    if (!queryString) return path

    // Check if path already has query params
    const separator = path.includes('?') ? '&' : '?'
    return `${path}${separator}${queryString}`
  }

  /**
   * Get current query string
   */
  const queryString = useMemo(() => {
    const str = currentParams.toString()
    return str ? `?${str}` : ''
  }, [currentParams])

  /**
   * Get a specific param value
   */
  const getParam = (key: string) => currentParams.get(key)

  /**
   * Check if a param exists and equals a value
   */
  const hasParam = (key: string, value?: string) => {
    const paramValue = currentParams.get(key)
    if (value !== undefined) {
      return paramValue === value
    }
    return paramValue !== null
  }

  return {
    currentParams,
    withParams,
    queryString,
    getParam,
    hasParam
  }
}

/**
 * Convenience hook specifically for demo mode
 */
export function useDemoMode() {
  const { hasParam, withParams, queryString } = usePreserveQueryParams(['demo'])

  return {
    isDemoMode: hasParam('demo', 'true'),
    withDemoParam: (path: string) => withParams(path),
    demoQueryString: queryString
  }
}
